import { h } from 'preact';
import { useState } from 'preact/hooks';

interface ToolActivity {
  type: 'start' | 'end' | 'progress';
  tool: string;
  input?: string;
}

const TOOL_ICONS: Record<string, string> = {
  // Claude (Anthropic Agent SDK) tool names
  Read: '👁',
  Glob: '🔍',
  Grep: '🔍',
  Bash: '▶',
  MultilineCommand: '▶',
  Write: '✏',
  Edit: '✏',
  Agent: '◈',
  WebFetch: '◌',
  WebSearch: '◌',
  EnterWorktree: '⌥',
  ExitWorktree: '⌥',
  TodoWrite: '☐',
  // Gemini (GeminiToolExecutorService) tool names — mirror Claude icons by purpose
  read_file: '👁',
  search_files: '🔍',
  search_content: '🔍',
  write_file: '✏',
  edit_file: '✏',
  run_shell: '▶',
  browser_navigate: '🌐',
  browser_snapshot: '🌐',
  browser_screenshot: '🌐',
  browser_click: '🌐',
  browser_fill: '🌐',
  browser_press_key: '🌐',
  browser_evaluate: '🌐',
};

function toolIcon(tool: string): string {
  return TOOL_ICONS[tool] ?? '⚙';
}

/** Strip /home/akrsmv/projects/ prefix, keep project/dir/file.ext */
function shortPath(input: string): string {
  return input.replace(/^\/home\/[^/]+\/projects\//, '');
}

/** True if the input looks like a file path (not a shell command) */
function isFilePath(tool: string, input: string): boolean {
  if (!input) return false;
  const fileTools = ['Read', 'Glob', 'Grep', 'Write', 'Edit'];
  if (!fileTools.includes(tool)) return false;
  return input.startsWith('/') || input.startsWith('~/');
}

function openFile(path: string) {
  window.dispatchEvent(new CustomEvent('open-file-viewer', { detail: { path } }));
}

/** Build "Read 3 · Bash 2 · Write 1" summary from end-type activities */
function buildSummary(activities: ToolActivity[]): string {
  const counts: Record<string, number> = {};
  for (const a of activities) {
    if (a.type !== 'end' || !a.input) continue;
    const key = a.tool === 'MultilineCommand' ? 'Bash' : a.tool;
    counts[key] = (counts[key] ?? 0) + 1;
  }
  const parts = Object.entries(counts).map(([tool, n]) => `${tool} ${n}`);
  return parts.join(' · ');
}

/** Total unique tool executions (count of 'end' events with non-empty input) */
function countTools(activities: ToolActivity[]): number {
  return activities.filter((a) => a.type === 'end' && a.input).length;
}

interface ToolTraceProps {
  text: string;
  /** If true, show as "live" (in-progress, no collapse button initially). Default: false */
  live?: boolean;
}

export function ToolTrace({ text, live = false }: ToolTraceProps) {
  const [expanded, setExpanded] = useState(false);

  let activities: ToolActivity[] = [];
  let parseError = false;
  try {
    const parsed = JSON.parse(text);
    if (Array.isArray(parsed)) activities = parsed;
    else parseError = true;
  } catch {
    parseError = true;
  }

  if (parseError) {
    return (
      <div class="tool-trace tool-trace--error">
        <span class="tool-trace-icon">⚙</span>
        <span class="tool-trace-label">tool activity (unreadable)</span>
      </div>
    );
  }

  const total = countTools(activities);
  const summary = total > 2 ? buildSummary(activities) : null;
  const label = summary ?? `${total} tool${total === 1 ? '' : 's'}`;

  // Only show 'end' events that have meaningful input
  const rows = activities.filter((a) => a.type === 'end' && a.input);

  return (
    <div class={`tool-trace${expanded ? ' tool-trace--expanded' : ''}${live ? ' tool-trace--live' : ''}`}>
      <button
        class="tool-trace-header"
        onClick={() => setExpanded((v) => !v)}
        aria-expanded={expanded}
        title={expanded ? 'Collapse tool activity' : 'Expand tool activity'}
      >
        <span class="tool-trace-chevron">{expanded ? '▾' : '▸'}</span>
        <span class="tool-trace-icon">⚙</span>
        <span class="tool-trace-label">{label}</span>
        {live && <span class="tool-trace-live-dot" aria-label="in progress" />}
      </button>

      {expanded && (
        <ul class="tool-trace-rows" aria-label="Tool activities">
          {rows.map((a, i) => {
            const icon = toolIcon(a.tool);
            const isPath = isFilePath(a.tool, a.input ?? '');
            const display = isPath ? shortPath(a.input ?? '') : (a.input ?? '').slice(0, 120);
            const toolName = a.tool === 'MultilineCommand' ? 'Bash' : a.tool;

            return (
              <li key={i} class="tool-trace-row">
                <span class="tool-icon" aria-hidden="true">{icon}</span>
                <span class="tool-name">{toolName}</span>
                {isPath ? (
                  <button
                    class="tool-path tool-path--link"
                    onClick={(e) => { e.stopPropagation(); openFile(a.input ?? ''); }}
                    title={`Open ${a.input}`}
                  >
                    {display}
                  </button>
                ) : (
                  <span class="tool-path" title={a.input}>{display}</span>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
